// ============================================================================
// Componente: FiscalAutomacaoAuditoria.js
// Objetivo: Interface para Auditoria Fiscal (SPED vs SAP), Extração e Histórico.
// Conteúdo embutido: quando `embedded` é true, é renderizada DENTRO do casco
//           Fiscal.js (que já traz Sidebar + cabeçalho), então não desenha
//           casco próprio. Default false = página inteira (compatibilidade).
// ============================================================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import {
    FileSearch, Download, AlertCircle, CheckCircle2, ArrowLeft, Timer,
    UploadCloud, Database, Plus, X, Info, FileText, History, Unlock,
    ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, HelpCircle,
    Loader2,FileSpreadsheet
} from 'lucide-react';
import HelpCenter from '../../HelpCenter';
import FiscalVerificacaoAliquotas from './FiscalVerificacaoAliquotas';

const AutomacaoAuditoria = ({ embedded = false }) => {
    const navigate = useNavigate();
    
    // Controle de Layout
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [activeTab, setActiveTab] = useState('auditoria');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Regras de Perfil do Usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';

    const [carregando, setCarregando] = useState(false);
    const [mensagem, setMensagem] = useState(null);
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando início...');

    // Estados da Auditoria
    const [arquivosMovimento, setArquivosMovimento] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const [isPolling, setIsPolling] = useState(false);
    const [executionId, setExecutionId] = useState(null);

    // Estados Soft Lock e Histórico
    const [modalTravaAberta, setModalTravaAberta] = useState(false);
    const [historicoAuditoria, setHistoricoAuditoria] = useState([]);

    // Filtros Histórico
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');
    
    // Ordenação da Tabela
    const [ordenacao, setOrdenacao] = useState({ coluna: 'DATA_MOVIMENTO', direcao: 'desc' });
    const [filtroLojaHistorico, setFiltroLojaHistorico] = useState('');
    const [filtroStatusHistorico, setFiltroStatusHistorico] = useState('');

    // Paginação Histórico
    const [paginaAtualHistorico, setPaginaAtualHistorico] = useState(1);
    const itensPorPagina = 30;

    // Extração
    const [datasSelecionadas, setDatasSelecionadas] = useState([]);
    const [dataInput, setDataInput] = useState('');
    const [lojasDisponiveis, setLojasDisponiveis] = useState([]);
    const [lojasSelecionadas, setLojasSelecionadas] = useState([]);
    const [lojaInput, setLojaInput] = useState('');

    // Cores - Fiscal (Roxo)
    const corModulo = '#7c3aed';
    const corFundoClaro = '#f5f3ff';
    const corBordaClara = '#ddd6fe';

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        carregarLojas();
        carregarHistorico();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setPaginaAtualHistorico(1);
    }, [filtroDataInicio, filtroDataFim, filtroLojaHistorico, filtroStatusHistorico, ordenacao]);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    const carregarLojas = async () => {
        try {
            const res = await fetch('/api/fiscal/lojas-cadastro', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            if (res.ok) setLojasDisponiveis(await res.json());
        } catch (e) {}
    };

    const carregarHistorico = async () => {
        try {
            const res = await fetch('/api/fiscal/historico-auditoria', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            if (res.ok) setHistoricoAuditoria(await res.json());
        } catch (e) {}
    };

    useEffect(() => {
        let interval;
        if (isPolling && executionId) {
            interval = setInterval(async () => {
                try {
                    const res = await fetch(`/api/fiscal/logs/${executionId}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                    });
                    const data = await res.json();

                    if (data.logs && data.logs.length > 0) {
                        const lastLog = data.logs[data.logs.length - 1];
                        const cleanLog = lastLog.replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s*-\s*INFO\s*-\s*|\[.*?\]\s*)/g, '');
                        setStatusText(cleanLog);

                        // Os marcadores de FIM são procurados no array INTEIRO e nas linhas
                        // CRUAS (não no `cleanLog`). Dois motivos, aprendidos na marra:
                        //  (1) o soft lock chega em duas linhas — o token [SOFT_LOCK_TRIGGERED]
                        //      do Python (stderr) e, depois, a mensagem "Soft Lock detectado"
                        //      que o Node emite no close — e a ÚLTIMA linha costuma ser a
                        //      segunda; olhar só `lastLog` deixava o sinal passar batido.
                        //  (2) o `cleanLog` REMOVE tudo entre colchetes, então buscar
                        //      [SOFT_LOCK_TRIGGERED] nele nunca casaria (era código morto, e
                        //      por isso o modal não abria e a tela ficava carregando infinito).
                        const logsCrus = data.logs.join('\n');
                        const houveSoftLock = logsCrus.includes('[SOFT_LOCK_TRIGGERED]') || logsCrus.includes('Soft Lock detectado');
                        const houveErro = logsCrus.includes('Ocorreu um erro no script Python') || logsCrus.includes('[ERRO FATAL]');

                        if (houveSoftLock) {
                            clearInterval(interval);
                            setIsPolling(false);
                            setCarregando(false);
                            setModalTravaAberta(true);
                        } else if (cleanLog.includes('Relatório gerado')) {
                            setProgress(95);
                            clearInterval(interval);
                            setIsPolling(false);
                            baixarRelatorioAuditoria(executionId);
                            carregarHistorico();
                        } else if (cleanLog.includes('Nenhuma divergência') || cleanLog.includes('A base bate 100%')) {
                            setProgress(100);
                            clearInterval(interval);
                            setIsPolling(false);
                            setStatusText('Auditoria Perfeita!');
                            setMensagem({ type: 'success', text: 'Nenhuma divergência encontrada. A base está correta!' });
                            setTimeout(() => {
                                setCarregando(false);
                                setProgress(0);
                            }, 3000);
                            carregarHistorico();
                        } else if (houveErro) {
                            // Sem este ramo, um erro do Python também deixava a tela carregando
                            // para sempre (o Node emite a mensagem de erro, mas o front não a lia).
                            clearInterval(interval);
                            setIsPolling(false);
                            setCarregando(false);
                            setMensagem({ type: 'error', text: 'Ocorreu um erro ao processar a auditoria. Confira os arquivos enviados e tente novamente.' });
                        }
                    }
                } catch (e) {}
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isPolling, executionId]);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files) setArquivosMovimento(Array.from(e.dataTransfer.files));
    };

    const handleFileSelect = (e) => {
        if (e.target.files) setArquivosMovimento(Array.from(e.target.files));
    };

    const removerArquivo = (idx) => {
        const n = [...arquivosMovimento];
        n.splice(idx, 1);
        setArquivosMovimento(n);
    };

    const handleAuditoria = async (e, bypass = false) => {
        if (e) e.preventDefault();
        if (arquivosMovimento.length === 0) return;

        setModalTravaAberta(false);
        setCarregando(true);
        setMensagem(null);
        setProgress(5);
        setStatusText('Enviando arquivos para o servidor...');

        const newExecutionId = Date.now().toString();
        setExecutionId(newExecutionId);

        const formData = new FormData();
        formData.append('executionId', newExecutionId);
        formData.append('forceBypass', bypass);
        arquivosMovimento.forEach(file => formData.append('arquivos_movimento', file));

        try {
            const response = await fetch('/api/fiscal/auditoria-fiscal', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: formData
            });

            if (response.ok || response.status === 202) {
                setStatusText('Processando dados no motor Python...');
                setProgress(15);
                setIsPolling(true);
            } else {
                const errorData = await response.json();
                setMensagem({ type: 'error', text: errorData.error || 'Erro ao processar.' });
                setCarregando(false);
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de conexão.' });
            setCarregando(false);
        }
    };

    const baixarRelatorioAuditoria = async (id) => {
        setStatusText('Baixando relatório de divergências...');
        try {
            const res = await fetch(`/api/fiscal/download-auditoria/${id}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });

            if (res.ok) {
                const contentType = res.headers.get('content-type');
                if (contentType && contentType.indexOf('application/json') !== -1) {
                    const data = await res.json();
                    setMensagem({ type: 'success', text: data.message });
                } else {
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'Relatorio_Auditoria_Final.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode.removeChild(link);
                    setMensagem({ type: 'success', text: 'Auditoria concluída! O relatório foi baixado.' });
                }
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro ao baixar o arquivo.' });
        } finally {
            setProgress(100);
            setStatusText('Finalizado!');
            setTimeout(() => {
                setCarregando(false);
                setProgress(0);
            }, 2000);
        }
    };

    const adicionarData = () => {
        if (dataInput && !datasSelecionadas.includes(dataInput)) {
            setDatasSelecionadas([...datasSelecionadas, dataInput]);
            setDataInput('');
        }
    };

    const removerData = (valor) => setDatasSelecionadas(datasSelecionadas.filter(d => d !== valor));

    const adicionarLoja = () => {
        if (!lojaInput) return;
        if (lojaInput === 'TODAS') {
            const todasLojas = lojasDisponiveis.map(l => ({
                cnpj: String(l.nr_cnpj_cpf),
                nome: l.nome_loja
            }));
            setLojasSelecionadas(todasLojas);
        } else {
            const jaExiste = lojasSelecionadas.some(l => String(l.cnpj) === String(lojaInput));
            if (!jaExiste) {
                const lojaInfo = lojasDisponiveis.find(l => String(l.nr_cnpj_cpf) === String(lojaInput));
                if (lojaInfo) {
                    setLojasSelecionadas([
                        ...lojasSelecionadas,
                        { cnpj: String(lojaInfo.nr_cnpj_cpf), nome: lojaInfo.nome_loja }
                    ]);
                }
            }
        }
        setLojaInput('');
    };

    const removerLoja = (cnpj) => setLojasSelecionadas(lojasSelecionadas.filter(l => l.cnpj !== cnpj));

    const handleExtracao = async (e) => {
        e.preventDefault();
        setMensagem(null);

        if (datasSelecionadas.length === 0 || lojasSelecionadas.length === 0) {
            setMensagem({ type: 'error', text: 'Adicione datas e lojas.' });
            return;
        }

        setCarregando(true);
        const newExecutionId = Date.now().toString();
        setProgress(20);
        setStatusText('Extraindo dados...');

        try {
            const response = await fetch('/api/fiscal/extracao-sovos', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    datas: datasSelecionadas,
                    cnpjs: lojasSelecionadas.map(l => l.cnpj),
                    executionId: newExecutionId
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setProgress(60);
                setStatusText('Transferindo...');
                const downloadRes = await fetch(`/api/fiscal/download-apuracao/${data.executionId}/${data.files[0]}`, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                });

                if (downloadRes.ok) {
                    const blob = await downloadRes.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', data.files[0]);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    setMensagem({ type: 'success', text: 'Extração concluída com sucesso!' });
                    setProgress(100);
                }
            } else {
                setMensagem({ type: 'error', text: data.error || 'Erro ao extrair.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de conexão.' });
        } finally {
            setCarregando(false);
            setTimeout(() => setProgress(0), 3000);
        }
    };

    const parseDataBR = (dataBR) => {
        if (!dataBR || typeof dataBR !== 'string') return null;
        const partes = dataBR.split('/');
        if (partes.length !== 3) return null;
        const [dia, mes, ano] = partes.map(Number);
        if (!dia || !mes || !ano) return null;
        const data = new Date(ano, mes - 1, dia);
        data.setHours(0, 0, 0, 0);
        return data;
    };

    const formatarDataParaChaveBR = (dataObj) => {
        const dia = String(dataObj.getDate()).padStart(2, '0');
        const mes = String(dataObj.getMonth() + 1).padStart(2, '0');
        const ano = dataObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
    };

    const lojasHistoricoOptions = useMemo(() => {
        const mapa = new Map();
        historicoAuditoria.forEach((item) => {
            const nome = item.NOME_LOJA || item.CNPJ_LOJA || '';
            if (nome && !mapa.has(nome)) mapa.set(nome, nome);
        });
        return Array.from(mapa.values()).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [historicoAuditoria]);

    const historicoFiltrado = useMemo(() => {
        let filtrado = historicoAuditoria.filter((item) => {
            let matchData = true;
            if (filtroDataInicio || filtroDataFim) {
                const dataItem = parseDataBR(item.DATA_MOVIMENTO);
                if (dataItem) {
                    if (filtroDataInicio) {
                        const dataInicio = new Date(filtroDataInicio + 'T00:00:00');
                        if (dataItem < dataInicio) matchData = false;
                    }
                    if (filtroDataFim) {
                        const dataFim = new Date(filtroDataFim + 'T23:59:59');
                        if (dataItem > dataFim) matchData = false;
                    }
                } else {
                    matchData = false;
                }
            }
            const nomeLoja = (item.NOME_LOJA || item.CNPJ_LOJA || '').toString();
            const matchLoja = !filtroLojaHistorico || nomeLoja === filtroLojaHistorico;
            const status = (item.STATUS_RESULTADO || '').toString().toLowerCase();
            const matchStatus = !filtroStatusHistorico || status === filtroStatusHistorico.toLowerCase();

            return matchData && matchLoja && matchStatus;
        });

        filtrado.sort((a, b) => {
            if (ordenacao.coluna === 'DATA_MOVIMENTO') {
                const dataA = parseDataBR(a.DATA_MOVIMENTO) || new Date(0);
                const dataB = parseDataBR(b.DATA_MOVIMENTO) || new Date(0);
                return ordenacao.direcao === 'asc' ? dataA - dataB : dataB - dataA;
            } else if (ordenacao.coluna === 'NOME_LOJA') {
                const nomeA = (a.NOME_LOJA || a.CNPJ_LOJA || '').toString().toLowerCase();
                const nomeB = (b.NOME_LOJA || b.CNPJ_LOJA || '').toString().toLowerCase();
                if (nomeA < nomeB) return ordenacao.direcao === 'asc' ? -1 : 1;
                if (nomeA > nomeB) return ordenacao.direcao === 'asc' ? 1 : -1;
                return 0;
            }
            return 0;
        });

        return filtrado;
    }, [historicoAuditoria, filtroDataInicio, filtroDataFim, filtroLojaHistorico, filtroStatusHistorico, ordenacao]);

    const handleSort = (coluna) => {
        if (ordenacao.coluna === coluna) {
            setOrdenacao({ coluna, direcao: ordenacao.direcao === 'asc' ? 'desc' : 'asc' });
        } else {
            setOrdenacao({ coluna, direcao: 'asc' });
        }
    };

    const renderSortIcon = (coluna) => {
        if (ordenacao.coluna !== coluna) return <ArrowUpDown size={14} style={{ marginLeft: '6px', opacity: 0.4 }} />;
        return ordenacao.direcao === 'asc' ? 
            <ArrowUp size={14} style={{ marginLeft: '6px', color: corModulo }} /> : 
            <ArrowDown size={14} style={{ marginLeft: '6px', color: corModulo }} />;
    };

    const totalPaginasHistorico = Math.max(1, Math.ceil(historicoFiltrado.length / itensPorPagina));

    const historicoPaginado = useMemo(() => {
        const inicio = (paginaAtualHistorico - 1) * itensPorPagina;
        const fim = inicio + itensPorPagina;
        return historicoFiltrado.slice(inicio, fim);
    }, [historicoFiltrado, paginaAtualHistorico]);

    const resumoDiasELojasNaoAuditados = useMemo(() => {
        const dataInicio = new Date(2026, 0, 1);
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const lojasUnicas = Array.from(
            new Set(historicoAuditoria.map(item => item.NOME_LOJA || item.CNPJ_LOJA).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

        const paresAuditados = new Set(
            historicoAuditoria
                .filter(item => (item.DATA_MOVIMENTO && (item.NOME_LOJA || item.CNPJ_LOJA) && item.STATUS_RESULTADO === 'Base 100% Correta'))
                .map(item => `${item.DATA_MOVIMENTO}|||${item.NOME_LOJA || item.CNPJ_LOJA}`)
        );

        const diasSemQualquerAuditoria = [];
        const lojasComPendencias = new Set();
        let totalPendencias = 0;
        const cursor = new Date(dataInicio);

        while (cursor <= hoje) {
            const dataBR = formatarDataParaChaveBR(cursor);
            let existeAlgumaAuditoriaNoDia = false;
            lojasUnicas.forEach((loja) => {
                const chave = `${dataBR}|||${loja}`;
                if (paresAuditados.has(chave)) {
                    existeAlgumaAuditoriaNoDia = true;
                } else {
                    lojasComPendencias.add(loja);
                    totalPendencias += 1;
                }
            });
            if (!existeAlgumaAuditoriaNoDia) diasSemQualquerAuditoria.push(dataBR);
            cursor.setDate(cursor.getDate() + 1);
        }

        return {
            diasSemQualquerAuditoria,
            lojasComPendencias: Array.from(lojasComPendencias),
            totalPendencias
        };
    }, [historicoAuditoria]);

    const limparAuditoria = () => {
        setArquivosMovimento([]);
        setMensagem(null);
        setProgress(0);
        setStatusText('Aguardando início...');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const limparExtracao = () => {
        setDatasSelecionadas([]);
        setDataInput('');
        setLojasSelecionadas([]);
        setLojaInput('');
        setMensagem(null);
        setProgress(0);
        setStatusText('Aguardando início...');
    };

    const getProgressBarColor = () => {
        if (mensagem?.type === 'error') return '#ef4444'; // Vermelho
        if (mensagem?.type === 'success') return '#10b981'; // Verde
        return corModulo; // Roxo Padrão (Processando)
    };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%' }}>

                {/* CABEÇALHO PREMIUM — só na versão página inteira; embutido herda o do Fiscal.js */}
                {!embedded && (
                <div style={{
                    height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/fiscal')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                            <span style={{ margin: '0 4px' }}>/</span> Fiscal <span style={{ margin: '0 4px' }}>/</span> Auditoria
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FileSearch size={24} color={corModulo} /> Auditoria SPED vs SAP
                            </h2>
                            <button 
                                type="button"
                                onClick={() => setIsHelpOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: corFundoClaro, color: corModulo, border: `1px solid ${corBordaClara}`, borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                            </button>
                        </div>
                    </div>
                    
                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: embedded ? 0 : (isMobile ? '20px' : '40px') }} className="max-w-6xl mx-auto">
                    
                    <div style={{ backgroundColor: '#fff', padding: isMobile ? '20px' : '30px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                        
                        {/* ABAS MODERNIZADAS */}
                        <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '30px' }}>
                            <button
                                onClick={() => { setActiveTab('auditoria'); setMensagem(null); }}
                                style={{ flex: 1, padding: '12px', fontSize: '15px', fontWeight: '600', border: 'none', borderBottom: activeTab === 'auditoria' ? `3px solid ${corModulo}` : 'none', color: activeTab === 'auditoria' ? corModulo : '#64748b', backgroundColor: 'transparent', cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                1. Auditoria Automática
                            </button>
                            <button
                                onClick={() => { setActiveTab('extracao'); setMensagem(null); }}
                                style={{ flex: 1, padding: '12px', fontSize: '15px', fontWeight: '600', border: 'none', borderBottom: activeTab === 'extracao' ? `3px solid ${corModulo}` : 'none', color: activeTab === 'extracao' ? corModulo : '#64748b', backgroundColor: 'transparent', cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                2. Extração Direta
                            </button>
                            <button
                                onClick={() => { setActiveTab('historico'); setMensagem(null); }}
                                style={{ flex: 1, padding: '12px', fontSize: '15px', fontWeight: '600', border: 'none', borderBottom: activeTab === 'historico' ? `3px solid ${corModulo}` : 'none', color: activeTab === 'historico' ? corModulo : '#64748b', backgroundColor: 'transparent', cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                3. Histórico e Controle
                            </button>
                            <button
                                onClick={() => { setActiveTab('aliquotas'); setMensagem(null); }}
                                style={{ flex: 1, padding: '12px', fontSize: '15px', fontWeight: '600', border: 'none', borderBottom: activeTab === 'aliquotas' ? `3px solid ${corModulo}` : 'none', color: activeTab === 'aliquotas' ? corModulo : '#64748b', backgroundColor: 'transparent', cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                4. Verificação de Alíquotas
                            </button>
                        </div>

                        {/* ABA 1: AUDITORIA */}
                        {activeTab === 'auditoria' && (
                            <form onSubmit={(e) => handleAuditoria(e, false)}>
                                <div style={{ backgroundColor: corFundoClaro, borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px' }}>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#5b21b6', fontSize: '15px', fontWeight: '700', marginBottom: '8px', marginTop: 0 }}>
                                        <Info size={18} /> Proteção Ativa
                                    </h4>
                                    <p style={{ color: '#4c1d95', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                                        O sistema bloqueará automaticamente a re-execução de lojas e dias que já foram auditados e marcados como <strong>Base 100% Correta</strong>. Arraste os arquivos .xlsx ou .csv gerados pelo SAP abaixo para começar a cruzar com o banco de dados.
                                    </p>
                                </div>

                                <div style={{ marginBottom: '24px' }}>
                                    <div
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        onClick={() => fileInputRef.current.click()}
                                        style={{ 
                                            border: isDragging ? `2px dashed ${corModulo}` : `2px dashed #cbd5e1`, 
                                            borderRadius: '12px', padding: '40px', textAlign: 'center', 
                                            backgroundColor: isDragging ? '#ede9fe' : '#f8fafc', 
                                            cursor: 'pointer', transition: 'all 0.2s' 
                                        }}
                                    >
                                        <input type="file" multiple accept=".xlsx,.csv" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileSelect} disabled={carregando} />
                                        
                                        {arquivosMovimento.length === 0 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ backgroundColor: '#f1f5f9', padding: '16px', borderRadius: '50%', color: '#94a3b8' }}>
                                                    <UploadCloud size={32} />
                                                </div>
                                                <div>
                                                    <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#334155' }}>Arraste os arquivos aqui ou clique para buscar</p>
                                                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>Suporta múltiplos arquivos .xlsx ou .csv</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ backgroundColor: corFundoClaro, padding: '16px', borderRadius: '50%', color: corModulo }}>
                                                    <FileSpreadsheet size={32} />
                                                </div>
                                                <div>
                                                    <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#0f172a' }}>Arquivos Prontos para Auditoria</p>
                                                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: corModulo }}>{arquivosMovimento.length} arquivo(s) selecionado(s)</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {arquivosMovimento.length > 0 && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px', marginTop: '20px' }}>
                                            {arquivosMovimento.map((file, idx) => (
                                                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                                                        <FileText size={18} color={corModulo} />
                                                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {file.name}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); removerArquivo(idx); }}
                                                        style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                                    <button
                                        type="button"
                                        onClick={limparAuditoria}
                                        disabled={carregando || arquivosMovimento.length === 0}
                                        style={{ padding: '12px 24px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '600', border: '1px solid #cbd5e1', cursor: (carregando || arquivosMovimento.length === 0) ? 'not-allowed' : 'pointer' }}
                                    >
                                        Limpar Tudo
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={carregando || arquivosMovimento.length === 0}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '8px', backgroundColor: carregando || arquivosMovimento.length === 0 ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '600', border: 'none', cursor: carregando || arquivosMovimento.length === 0 ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.2)' }}
                                    >
                                        {carregando ? <><Loader2 size={18} className="animate-spin" /> Auditando...</> : <><Database size={18} /> Iniciar Auditoria SPED</>}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* ABA 2: EXTRAÇÃO */}
                        {activeTab === 'extracao' && (
                            <form onSubmit={handleExtracao}>
                                <div style={{ backgroundColor: corFundoClaro, borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px' }}>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#5b21b6', fontSize: '15px', fontWeight: '700', marginBottom: '8px', marginTop: 0 }}>
                                        <Database size={18} /> Consulta Direta (Sovos)
                                    </h4>
                                    <p style={{ color: '#4c1d95', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                                        Selecione as datas e as lojas para extrair os valores totais diretamente da base Sovos, agrupados por data e loja. Ideal para conferências pontuais.
                                    </p>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                                    
                                    {/* Datas */}
                                    <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px', display: 'block' }}>1. Selecionar Datas</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <input 
                                                type="date" 
                                                value={dataInput} 
                                                onChange={(e) => setDataInput(e.target.value)} 
                                                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }} 
                                                disabled={carregando}
                                            />
                                            <button 
                                                type="button" 
                                                onClick={adicionarData} 
                                                disabled={!dataInput || carregando}
                                                style={{ padding: '0 20px', backgroundColor: !dataInput ? '#e2e8f0' : corModulo, color: !dataInput ? '#94a3b8' : '#fff', borderRadius: '8px', cursor: !dataInput ? 'default' : 'pointer', border: 'none', transition: 'all 0.2s' }}
                                            >
                                                <Plus size={20} />
                                            </button>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}>
                                            {datasSelecionadas.map((dt) => (
                                                <span key={dt} style={{ backgroundColor: corFundoClaro, color: corModulo, padding: '6px 12px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${corBordaClara}` }}>
                                                    {dt}
                                                    <X size={14} style={{ cursor: 'pointer' }} onClick={() => removerData(dt)} />
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Lojas */}
                                    <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>2. Selecionar Lojas</label>
                                            {lojasSelecionadas.length > 0 && (
                                                <button type="button" onClick={() => setLojasSelecionadas([])} style={{ fontSize: '12px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>
                                                    Limpar Lojas
                                                </button>
                                            )}
                                        </div>

                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <select 
                                                value={lojaInput} 
                                                onChange={(e) => setLojaInput(e.target.value)} 
                                                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', fontSize: '14px', outline: 'none' }}
                                                disabled={carregando}
                                            >
                                                <option value="">Selecione uma loja...</option>
                                                {lojasSelecionadas.length !== lojasDisponiveis.length && (
                                                    <option value="TODAS" style={{ fontWeight: 'bold', color: corModulo }}>
                                                        + ADICIONAR TODAS AS LOJAS
                                                    </option>
                                                )}
                                                {lojasDisponiveis
                                                    .filter(l => !lojasSelecionadas.some(sel => String(sel.cnpj) === String(l.nr_cnpj_cpf)))
                                                    .map((loja) => (
                                                        <option key={loja.nr_cnpj_cpf} value={loja.nr_cnpj_cpf}>
                                                            {loja.nome_loja}
                                                        </option>
                                                    ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={adicionarLoja}
                                                disabled={!lojaInput || carregando}
                                                style={{ padding: '0 20px', backgroundColor: !lojaInput ? '#e2e8f0' : corModulo, color: !lojaInput ? '#94a3b8' : '#fff', borderRadius: '8px', cursor: !lojaInput ? 'default' : 'pointer', border: 'none', transition: 'all 0.2s' }}
                                            >
                                                <Plus size={20} />
                                            </button>
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px', maxHeight: '150px', overflowY: 'auto', paddingRight: '4px' }}>
                                            {lojasSelecionadas.map((loja) => (
                                                <span key={loja.cnpj} style={{ backgroundColor: corFundoClaro, color: corModulo, padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${corBordaClara}` }}>
                                                    {loja.nome}
                                                    <X size={14} style={{ cursor: 'pointer' }} onClick={() => removerLoja(loja.cnpj)} />
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                                    <button
                                        type="button"
                                        onClick={limparExtracao}
                                        disabled={carregando || (datasSelecionadas.length === 0 && lojasSelecionadas.length === 0)}
                                        style={{ padding: '12px 24px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '600', border: '1px solid #cbd5e1', cursor: (carregando || (datasSelecionadas.length === 0 && lojasSelecionadas.length === 0)) ? 'not-allowed' : 'pointer' }}
                                    >
                                        Limpar Extração
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={carregando || datasSelecionadas.length === 0 || lojasSelecionadas.length === 0}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '8px', backgroundColor: carregando || datasSelecionadas.length === 0 || lojasSelecionadas.length === 0 ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '600', border: 'none', cursor: carregando || datasSelecionadas.length === 0 || lojasSelecionadas.length === 0 ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.2)' }}
                                    >
                                        {carregando ? <><Loader2 size={18} className="animate-spin" /> Extraindo...</> : <><Download size={18} /> Extrair Banco de Dados</>}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* ABA 3: HISTÓRICO */}
                        {activeTab === 'historico' && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', color: '#1e293b' }}>
                                    <History size={24} color={corModulo} />
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Painel de Controle (Auditorias)</h3>
                                </div>

                                {/* Resumo de Pendências */}
                                <div style={{
                                    marginBottom: '24px', padding: '20px', borderRadius: '12px',
                                    backgroundColor: resumoDiasELojasNaoAuditados.totalPendencias > 0 ? '#fff7ed' : '#ecfdf5',
                                    border: `1px solid ${resumoDiasELojasNaoAuditados.totalPendencias > 0 ? '#fdba74' : '#a7f3d0'}`
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                        {resumoDiasELojasNaoAuditados.totalPendencias > 0 ? (
                                            <AlertCircle size={22} color="#c2410c" />
                                        ) : (
                                            <CheckCircle2 size={22} color="#047857" />
                                        )}
                                        <strong style={{ fontSize: '16px', color: resumoDiasELojasNaoAuditados.totalPendencias > 0 ? '#9a3412' : '#065f46' }}>
                                            {resumoDiasELojasNaoAuditados.totalPendencias > 0
                                                ? 'Existem pendências de auditoria desde 01/01/2026'
                                                : 'Parabéns! Nenhuma pendência de auditoria encontrada.'}
                                        </strong>
                                    </div>

                                    {resumoDiasELojasNaoAuditados.totalPendencias > 0 && (
                                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                                            {/* Dias Pendentes */}
                                            <div style={{ backgroundColor: 'rgba(255,255,255,0.6)', padding: '16px', borderRadius: '8px' }}>
                                                <div style={{ fontSize: '14px', color: '#7c2d12', marginBottom: '12px' }}>
                                                    <strong>Dias sem NENHUMA auditoria:</strong> {resumoDiasELojasNaoAuditados.diasSemQualquerAuditoria.length}
                                                </div>
                                                {resumoDiasELojasNaoAuditados.diasSemQualquerAuditoria.length > 0 && (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '110px', overflowY: 'auto' }}>
                                                        {resumoDiasELojasNaoAuditados.diasSemQualquerAuditoria.map((dia) => (
                                                            <span key={dia} style={{ backgroundColor: '#ffedd5', color: '#9a3412', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' }}>
                                                                {dia}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Lojas Pendentes */}
                                            <div style={{ backgroundColor: 'rgba(255,255,255,0.6)', padding: '16px', borderRadius: '8px' }}>
                                                <div style={{ fontSize: '14px', color: '#7c2d12', marginBottom: '12px' }}>
                                                    <strong>Lojas pendentes em pelo menos um dia:</strong> {resumoDiasELojasNaoAuditados.lojasComPendencias.length}
                                                </div>
                                                {resumoDiasELojasNaoAuditados.lojasComPendencias.length > 0 && (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '110px', overflowY: 'auto' }}>
                                                        {resumoDiasELojasNaoAuditados.lojasComPendencias.map((loja) => (
                                                            <span key={loja} style={{ backgroundColor: '#ffedd5', color: '#9a3412', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' }}>
                                                                {loja}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Filtros da Tabela */}
                                <div style={{
                                    display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '16px',
                                    marginBottom: '20px', padding: '20px', backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0', borderRadius: '12px'
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>Data Início</label>
                                        <input
                                            type="date" value={filtroDataInicio} onChange={(e) => setFiltroDataInicio(e.target.value)}
                                            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>Data Fim</label>
                                        <input
                                            type="date" value={filtroDataFim} onChange={(e) => setFiltroDataFim(e.target.value)}
                                            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>Loja</label>
                                        <select
                                            value={filtroLojaHistorico} onChange={(e) => setFiltroLojaHistorico(e.target.value)}
                                            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff', outline: 'none' }}
                                        >
                                            <option value="">Todas as Lojas</option>
                                            {lojasHistoricoOptions.map((loja) => (
                                                <option key={loja} value={loja}>{loja}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>Status</label>
                                        <select
                                            value={filtroStatusHistorico} onChange={(e) => setFiltroStatusHistorico(e.target.value)}
                                            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff', outline: 'none' }}
                                        >
                                            <option value="">Todos os Status</option>
                                            <option value="base 100% correta">Base 100% Correta</option>
                                            <option value="divergências encontradas">Divergências</option>
                                        </select>
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                                        Exibindo <strong>{historicoFiltrado.length}</strong> registros
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFiltroDataInicio(''); setFiltroDataFim(''); setFiltroLojaHistorico(''); setFiltroStatusHistorico('');
                                            setOrdenacao({ coluna: 'DATA_MOVIMENTO', direcao: 'desc' }); setPaginaAtualHistorico(1);
                                        }}
                                        style={{ fontSize: '13px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                                    >
                                        Limpar filtros
                                    </button>
                                </div>

                                {/* Tabela de Histórico */}
                                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                                        <thead style={{ backgroundColor: '#f8fafc', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                                            <tr>
                                                <th onClick={() => handleSort('DATA_MOVIMENTO')} style={{ padding: '16px', fontWeight: '700', cursor: 'pointer', userSelect: 'none' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>Data Movimento {renderSortIcon('DATA_MOVIMENTO')}</div>
                                                </th>
                                                <th onClick={() => handleSort('NOME_LOJA')} style={{ padding: '16px', fontWeight: '700', cursor: 'pointer', userSelect: 'none' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>Loja {renderSortIcon('NOME_LOJA')}</div>
                                                </th>
                                                <th style={{ padding: '16px', fontWeight: '700' }}>Usuário</th>
                                                <th style={{ padding: '16px', fontWeight: '700' }}>Realizado Em</th>
                                                <th style={{ padding: '16px', fontWeight: '700' }}>Status da Auditoria</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historicoPaginado.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                                                        Nenhum registro encontrado para os filtros informados.
                                                    </td>
                                                </tr>
                                            ) : (
                                                historicoPaginado.map((hist) => (
                                                    <tr key={hist.ID_CONTROLE} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: '#fff', transition: 'background-color 0.2s', ':hover': { backgroundColor: '#f8fafc' } }}>
                                                        <td style={{ padding: '16px', fontWeight: '600', color: '#1e293b' }}>
                                                            {hist.DATA_MOVIMENTO}
                                                        </td>
                                                        <td style={{ padding: '16px', color: '#475569', fontWeight: '500' }}>
                                                            {hist.NOME_LOJA || hist.CNPJ_LOJA}
                                                        </td>
                                                        <td style={{ padding: '16px', color: '#64748b' }}>
                                                            {hist.NOME_USUARIO}
                                                        </td>
                                                        <td style={{ padding: '16px', color: '#94a3b8', fontSize: '13px' }}>
                                                            {hist.DATA_EXECUCAO}
                                                        </td>
                                                        <td style={{ padding: '16px' }}>
                                                            <span style={{
                                                                padding: '6px 12px',
                                                                borderRadius: '999px',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                backgroundColor: hist.QTD_DIVERGENCIAS === 0 ? '#dcfce7' : '#fee2e2',
                                                                color: hist.QTD_DIVERGENCIAS === 0 ? '#166534' : '#991b1b',
                                                                border: `1px solid ${hist.QTD_DIVERGENCIAS === 0 ? '#bbf7d0' : '#fecaca'}`
                                                            }}>
                                                                {hist.QTD_DIVERGENCIAS === 0 ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                                                                {hist.STATUS_RESULTADO}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Paginação */}
                                {historicoFiltrado.length > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
                                        <span style={{ fontSize: '14px', color: '#64748b' }}>
                                            Página <strong>{paginaAtualHistorico}</strong> de <strong>{totalPaginasHistorico}</strong>
                                        </span>

                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                type="button"
                                                onClick={() => setPaginaAtualHistorico(prev => Math.max(1, prev - 1))}
                                                disabled={paginaAtualHistorico === 1}
                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: paginaAtualHistorico === 1 ? '#f8fafc' : '#fff', color: paginaAtualHistorico === 1 ? '#94a3b8' : '#334155', fontWeight: '600', cursor: paginaAtualHistorico === 1 ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
                                            >
                                                <ChevronLeft size={16} /> Anterior
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => setPaginaAtualHistorico(prev => Math.min(totalPaginasHistorico, prev + 1))}
                                                disabled={paginaAtualHistorico === totalPaginasHistorico}
                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: paginaAtualHistorico === totalPaginasHistorico ? '#f8fafc' : '#fff', color: paginaAtualHistorico === totalPaginasHistorico ? '#94a3b8' : '#334155', fontWeight: '600', cursor: paginaAtualHistorico === totalPaginasHistorico ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
                                            >
                                                Próxima <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ABA 4: VERIFICAÇÃO DE ALÍQUOTAS (componente próprio) */}
                        {activeTab === 'aliquotas' && (
                            <FiscalVerificacaoAliquotas />
                        )}

                        {/* ALERTAS GERAIS E CARD DE PROGRESSO */}
                        {mensagem && !carregando && (
                            <div style={{ backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2', border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`, color: mensagem.type === 'success' ? '#059669' : '#ef4444', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', fontSize: '14px', fontWeight: '500' }}>
                                {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />} 
                                {mensagem.text}
                            </div>
                        )}
                    </div>

                    {/* CARD DE PROGRESSO */}
                    {(carregando || progress > 0) && (
                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {carregando && <Loader2 size={24} color={corModulo} className="animate-spin" />}
                                    {!carregando && mensagem?.type === 'success' && <CheckCircle2 size={24} color="#10b981" />}
                                    {!carregando && mensagem?.type === 'error' && <AlertCircle size={24} color="#ef4444" />}
                                    
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Status da Execução</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', marginTop: '2px' }}>{statusText}</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: '20px', fontWeight: '800', color: mensagem?.type === 'error' ? '#ef4444' : mensagem?.type === 'success' ? '#10b981' : corModulo }}>
                                    {progress}%
                                </span>
                            </div>

                            <div style={{ width: '100%', height: '12px', backgroundColor: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                                <div style={{ 
                                    width: `${progress}%`, 
                                    height: '100%', 
                                    backgroundColor: mensagem?.type === 'error' ? '#ef4444' : mensagem?.type === 'success' ? '#10b981' : corModulo,
                                    borderRadius: '99px',
                                    transition: 'width 0.5s ease-in-out, background-color 0.3s ease'
                                }} />
                            </div>
                        </div>
                    )}

                    {/* MODAL DE SOFT LOCK */}
                    {modalTravaAberta && (
                        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, backdropFilter: 'blur(4px)' }}>
                            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '30px', maxWidth: '450px', width: '90%', borderTop: '6px solid #f59e0b', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', color: '#d97706', marginBottom: '20px' }}>
                                    <div style={{ backgroundColor: '#fef3c7', padding: '10px', borderRadius: '50%' }}>
                                        <AlertCircle size={32} />
                                    </div>
                                    <h3 style={{ margin: 0, fontSize: '20px', color: '#1e293b', fontWeight: '800' }}>Soft Lock Ativado</h3>
                                </div>
                                <p style={{ color: '#475569', marginBottom: '30px', lineHeight: '1.6', fontSize: '15px' }}>
                                    O sistema detectou que alguns destes arquivos pertencem a dias e lojas que <strong>já foram auditados e marcados como "Base 100% Correta"</strong>.
                                    <br /><br />
                                    Como não há divergências pendentes no histórico, deseja forçar o reprocessamento destes dados e sobrepor a auditoria anterior?
                                </p>
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => setModalTravaAberta(false)}
                                        style={{ padding: '12px 20px', color: '#475569', fontWeight: '600', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer' }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={() => handleAuditoria(null, true)}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#f59e0b', color: '#fff', fontWeight: '700', borderRadius: '8px', border: 'none', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)' }}
                                    >
                                        <Unlock size={18} /> Forçar Auditoria
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* CHAMADA DO MANUAL COM FILTRO EXCLUSIVO PARA AUDITORIA FISCAL */}
            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial="auditoria-fiscal" 
                showOnlyIds={['auditoria-fiscal']} 
            />
        </div>
    );
};

export default AutomacaoAuditoria;